"use client";

import { User } from "@/types";
import { Room } from "../components/types";

// Base URL for API calls
const STRAPI_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:1338";

/**
 * Membership is the only access criterion for a room. The backend enforces it
 * on every room-management endpoint, and /rooms/user/me already filters on it,
 * so a room the user isn't a member of can only produce failing requests.
 */
export const isRoomMember = (
  room: Room,
  userId: number | undefined,
): boolean =>
  !!userId &&
  !!room.members?.some(
    (member: User | number) =>
      (typeof member === "object" ? member.id : member) === userId,
  );

/**
 * Rooms whose membership and metadata are derived server-side (support bot
 * rooms, applicant/agent channels — including hire-promoted ones, which keep
 * the applicant_agent relation but flip room_kind to 'standard'). The backend
 * refuses structural changes on these, so the UI must not offer them.
 */
export const isSystemManagedRoom = (room: Room): boolean =>
  !!room.applicant_agent ||
  room.room_kind === "support_bot" ||
  room.room_kind === "applicant";

/**
 * Add users to a room
 * @param {number} roomId - The ID of the room
 * @param {number[]} userIds - Array of user IDs to add to the room
 * @param {string} token - Auth token
 * @returns {Promise<Room>} The updated room object
 */
export const addUsersToRoom = async (
  roomId: number,
  userIds: number[],
  token: string,
): Promise<Room> => {
  try {
    const response = await fetch(`${STRAPI_URL}/api/rooms/${roomId}/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userIds }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add users: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error adding users to room:", error);
    throw error;
  }
};

/**
 * Remove a user from a room
 * @param {number} roomId - The ID of the room
 * @param {number} userId - The ID of the user to remove
 * @param {string} token - Auth token
 * @returns {Promise<Room|null>} The updated room object or null if room was deleted
 */
export const removeUserFromRoom = async (
  roomId: number,
  userId: number,
  token: string,
): Promise<Room | null> => {
  try {
    // Use POST with proper body instead of DELETE
    const response = await fetch(
      `${STRAPI_URL}/api/rooms/${roomId}/remove-user`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server error when removing user:", errorText);
      throw new Error(
        `Failed to remove user: ${response.status} - ${errorText}`,
      );
    }

    const result = await response.json();
    return result.data; // Will be null if the room was deleted
  } catch (error) {
    console.error("Error removing user from room:", error);
    throw error;
  }
};

/**
 * Update a room's avatar
 * @param {number} roomId - The ID of the room
 * @param {File} avatar - The avatar file to upload
 * @param {string} token - Auth token
 * @returns {Promise<Room>} The updated room object
 */
export const updateRoomAvatar = async (
  roomId: number,
  avatar: File,
  token: string,
): Promise<Room> => {
  try {
    const formData = new FormData();
    formData.append("files.avatar", avatar);

    const response = await fetch(`${STRAPI_URL}/api/rooms/${roomId}/avatar`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to update avatar: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error updating room avatar:", error);
    throw error;
  }
};
